import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * ProfileCompleteGuard - Redirects incomplete profiles to completion page
 *
 * Checks if authenticated user has completed their profile.
 * If not, redirects to /complete-profile with returnTo parameter.
 *
 * Excluded routes: routes that shouldn't trigger the redirect
 */
const ProfileCompleteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isAuthenticated, hasHydrated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Routes that should be accessible even with incomplete profile
  const excludedPaths = [
    '/complete-profile',
    '/login',
    '/auth/callback',
    '/auth/confirm',
    '/auth/magic',
    '/verify-email',
    '/reset-password',
    '/email-pending',
    '/onboarding',
    '/privacy',
    '/terms',
    '/cookies',
    '/safety',
    '/', // Landing page
  ];

  useEffect(() => {
    // Wait for auth to hydrate
    if (!hasHydrated) {
      console.log('[ProfileCompleteGuard] Waiting for auth to hydrate...');
      return;
    }

    // Only check authenticated users
    if (!isAuthenticated || !currentUser) {
      console.log('[ProfileCompleteGuard] User not authenticated, skipping check');
      return;
    }

    // Don't redirect from excluded paths
    const isExcluded = excludedPaths.some(path =>
      location.pathname === path || location.pathname.startsWith(path)
    );
    if (isExcluded) {
      console.log('[ProfileCompleteGuard] On excluded path:', location.pathname);
      return;
    }

    // Admin bypass
    if (currentUser.isAdmin) {
      console.log('[ProfileCompleteGuard] Admin user, skipping check');
      return;
    }

    // Check if profile is incomplete
    const isIncomplete = !currentUser.profileComplete;

    if (isIncomplete) {
      console.log('[ProfileCompleteGuard] Profile incomplete, redirecting with returnTo:', location.pathname + location.search);

      // Preserve the original destination
      const returnTo = location.pathname + location.search;
      navigate(`/complete-profile?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    } else {
      console.log('[ProfileCompleteGuard] Profile complete, allowing access');
    }
  }, [currentUser, isAuthenticated, hasHydrated, location.pathname, location.search, navigate]);

  return <>{children}</>;
};

export default ProfileCompleteGuard;
